import React from 'react'
import { 
  Clock, 
  CheckCircle, 
  Package, 
  Truck, 
  XCircle, 
  Share2,
  AlertCircle,
  Wifi,
  WifiOff
} from 'lucide-react'

interface OrderStatusIndicatorProps {
  status: string
  isRealtime?: boolean
  lastUpdate?: string | null
  className?: string
  showLabel?: boolean
  size?: 'sm' | 'md' | 'lg'
}

const OrderStatusIndicator: React.FC<OrderStatusIndicatorProps> = ({
  status,
  isRealtime = false,
  lastUpdate,
  className = '',
  showLabel = true,
  size = 'md'
}) => {
  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'pending':
        return {
          icon: Clock,
          color: 'text-yellow-500',
          bgColor: 'bg-yellow-500/10',
          borderColor: 'border-yellow-500/30',
          label: 'Payment Pending',
          description: 'Processing payment...'
        }
      case 'confirmed':
        return {
          icon: CheckCircle,
          color: 'text-blue-500',
          bgColor: 'bg-blue-500/10',
          borderColor: 'border-blue-500/30',
          label: 'Confirmed',
          description: 'Order confirmed!'
        }
      case 'preparing':
        return {
          icon: Package,
          color: 'text-orange-500',
          bgColor: 'bg-orange-500/10',
          borderColor: 'border-orange-500/30',
          label: 'Preparing',
          description: 'Being prepared with care...',
          animate: true
        }
      case 'ready':
        return {
          icon: Truck,
          color: 'text-green-500',
          bgColor: 'bg-green-500/10',
          borderColor: 'border-green-500/30',
          label: 'Ready for Pickup',
          description: 'Ready at the location!',
          pulse: true
        }
      case 'completed':
        return {
          icon: CheckCircle,
          color: 'text-green-600',
          bgColor: 'bg-green-600/10',
          borderColor: 'border-green-600/30',
          label: 'Completed',
          description: 'Order completed!'
        }
      case 'cancelled':
        return {
          icon: XCircle,
          color: 'text-red-500',
          bgColor: 'bg-red-500/10',
          borderColor: 'border-red-500/30',
          label: 'Cancelled',
          description: 'Order cancelled'
        }
      case 'transferred':
        return {
          icon: Share2,
          color: 'text-purple-500',
          bgColor: 'bg-purple-500/10',
          borderColor: 'border-purple-500/30',
          label: 'Transferred',
          description: 'Transferred to another user'
        }
      default:
        return {
          icon: AlertCircle,
          color: 'text-muted-foreground',
          bgColor: 'bg-muted/10',
          borderColor: 'border-border',
          label: 'Unknown',
          description: 'Status unknown'
        }
    }
  }

  const config = getStatusConfig(status)
  const Icon = config.icon

  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8'
  }

  const containerSizeClasses = {
    sm: 'p-2',
    md: 'p-3',
    lg: 'p-4'
  }

  const textSizeClasses = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg'
  }

  return (
    <div className={`inline-flex items-center space-x-3 ${className}`}>
      {/* Status Icon */}
      <div className={`
        flex items-center justify-center rounded-full border-2 
        ${config.bgColor} ${config.borderColor} ${containerSizeClasses[size]}
        ${config.pulse ? 'animate-pulse' : ''}
        relative
      `}>
        <Icon 
          className={`
            ${config.color} ${sizeClasses[size]}
            ${config.animate ? 'animate-spin' : ''}
          `} 
        />
        
        {/* Real-time indicator */}
        {isRealtime && (
          <div className="absolute -top-1 -right-1">
            <div className="relative">
              <Wifi className="w-3 h-3 text-green-400" />
              <div className="absolute inset-0 animate-ping">
                <Wifi className="w-3 h-3 text-green-400 opacity-50" />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Status Label */}
      {showLabel && (
        <div className="flex-1">
          <div className={`font-medium text-foreground ${textSizeClasses[size]}`}>
            {config.label}
          </div>
          <div className="text-muted-foreground text-sm">
            {config.description}
          </div>
          
          {/* Last Update */}
          {lastUpdate && (
            <div className="text-secret-text-tertiary text-xs mt-1">
              Updated {new Date(lastUpdate).toLocaleTimeString()}
            </div>
          )}
        </div>
      )}

      {/* Real-time Status */}
      {isRealtime && showLabel && (
        <div className="text-right">
          <div className="flex items-center space-x-1 text-green-400 text-xs">
            <Wifi className="w-3 h-3" />
            <span>Live</span>
          </div>
        </div>
      )}
    </div>
  )
}

// Progress indicator showing order flow
export const OrderProgressIndicator: React.FC<{ 
  currentStatus: string
  className?: string 
}> = ({ currentStatus, className = '' }) => {
  const steps = [
    { status: 'pending', label: 'Payment' },
    { status: 'confirmed', label: 'Confirmed' },
    { status: 'preparing', label: 'Preparing' },
    { status: 'ready', label: 'Ready' },
    { status: 'completed', label: 'Completed' }
  ]

  const getCurrentStepIndex = () => {
    const index = steps.findIndex(step => step.status === currentStatus)
    return index === -1 ? 0 : index
  }

  const currentStepIndex = getCurrentStepIndex()
  const isCompleted = (stepIndex: number) => stepIndex <= currentStepIndex
  const isCurrent = (stepIndex: number) => stepIndex === currentStepIndex

  if (['cancelled', 'transferred'].includes(currentStatus)) {
    return (
      <div className={`text-center ${className}`}>
        <OrderStatusIndicator 
          status={currentStatus} 
          showLabel={true} 
          size="sm"
        />
      </div>
    )
  }

  return (
    <div className={`${className}`}>
      <div className="flex items-center justify-between relative">
        {steps.map((step, index) => (
          <React.Fragment key={step.status}>
            {/* Step Circle */}
            <div className="flex flex-col items-center z-10">
              <div className={`
                w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all
                ${isCompleted(index) 
                  ? 'bg-secret-primary border-secret-primary text-white' 
                  : 'bg-secret-bg-secondary border-secret-border text-secret-text-secondary'
                }
                ${isCurrent(index) ? 'ring-2 ring-secret-primary/50 ring-offset-2 ring-offset-black' : ''}
              `}>
                {isCompleted(index) ? (
                  <CheckCircle className="w-4 h-4" />
                ) : (
                  <div className={`w-2 h-2 rounded-full ${
                    isCurrent(index) ? 'bg-secret-primary animate-pulse' : 'bg-secret-text-tertiary'
                  }`} />
                )}
              </div>
              
              <span className={`
                text-xs mt-2 font-medium transition-colors
                ${isCompleted(index) ? 'text-secret-primary' : 'text-muted-foreground'}
                ${isCurrent(index) ? 'text-foreground font-bold' : ''}
              `}>
                {step.label}
              </span>
            </div>

            {/* Connection Line */}
            {index < steps.length - 1 && (
              <div className={`
                flex-1 h-0.5 transition-colors mx-2
                ${isCompleted(index + 1) ? 'bg-secret-primary' : 'bg-secret-border'}
              `} />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  )
}

export default OrderStatusIndicator